import { useEffect, useState, useRef } from 'react';
import ZoomMtgEmbedded from '@zoom/meetingsdk/embedded';
import { eventApi } from '@/services/api';

/**
 * ZoomMeeting Component
 * 
 * This component handles joining Zoom meetings using the Zoom Meeting SDK.
 * All meeting data (meetingNumber, password, userName) must be provided as props.
 * 
 * Future integration: This component will receive meeting data from:
 * - Zoom API when creating new meetings
 * - Database when joining existing meetings
 * - User input for meeting details
 * 
 * Usage example:
 * <ZoomMeeting 
 *   meetingNumber="123456789"
 *   password="meeting123"
 *   userName="John Doe"
 *   userEmail="john@example.com"
 *   role={0}
 * />
 */

interface QuotaData {
  remaining_quota: number;
  quota: number;
  event_type: string;
}

interface QuotaApiResponse {
  status: string;
  data: {
    quotas: QuotaData[];
    grouped_quotas: Record<string, QuotaData[]>;
    summary: {
      career_coaching?: {
        total_quota: number;
        total_remaining: number;
      };
      professional_network_building?: {
        total_quota: number;
        total_remaining: number;
      };
    };
  };
}

interface ZoomMeetingProps {
  meetingNumber: string; // Required: Zoom meeting number
  password: string; // Required: Meeting password
  userName: string; // Required: Display name for the user
  userEmail?: string; // Optional: User's email address
  role?: number; // Optional: 0 = participant, 1 = host (defaults to 0)
  sessionId?: string; // Optional: booking/session ID for backend reference
  sessionTitle?: string; // Optional: Session title for modal (from schedules.title or event.title)
  scheduleAt?: string; // Optional: Schedule datetime in ISO format (e.g., "2025-05-06T12:00:00Z")
  zoomDuration?: number; // Optional: Duration in minutes (e.g., 120 = "2 hours")
  eventType?: string; // Optional: Event type to determine quota type ('online talks' -> 'career_coaching', 'prof networking' -> 'professional_network_building')
  scheduleId?: string | number; // Optional: Schedule ID for registration check and registration
  // Legacy props for backward compatibility
  sessionDate?: string; // Optional: Pre-formatted session date
  sessionTime?: string; // Optional: Pre-formatted session time
  sessionDuration?: string; // Optional: Pre-formatted session duration
}

/**
 * Format duration from minutes to readable format
 * Examples: 120 -> "2 hours", 90 -> "1 hour 30 minutes", 45 -> "45 minutes"
 */
const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0 minutes';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
  } else if (remainingMinutes === 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  } else {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
  }
};

/**
 * Parse schedule_at ISO string and format date
 * Example: "2025-05-06T12:00:00Z" -> "May 6, 2025"
 */
const formatDate = (scheduleAt: string): string => {
  try {
    const date = new Date(scheduleAt);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Parse schedule_at ISO string and format time
 * Example: "2025-05-06T12:00:00Z" -> "12:00 PM"
 */
const formatTime = (scheduleAt: string): string => {
  try {
    const date = new Date(scheduleAt);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

/**
 * Map event type to quota type
 */
const getQuotaType = (eventType?: string): string => {
  if (!eventType) return 'career_coaching'; // Default to career_coaching
  
  return eventType === 'prof networking' 
    ? 'professional_network_building' 
    : 'career_coaching';
};

const ZoomMeeting = ({ 
  meetingNumber,
  password, 
  userName, 
  userEmail = '', 
  role = 0,
  sessionTitle,
  scheduleAt,
  zoomDuration,
  eventType,
  scheduleId,
  sessionDate: legacySessionDate,
  sessionTime: legacySessionTime,
  sessionDuration: legacySessionDuration
}: ZoomMeetingProps) => {
  const [showModal, setShowModal] = useState(false);
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const meetingContainerRef = useRef<HTMLDivElement>(null);
  const sdkElementRef = useRef<HTMLDivElement>(null);
  const client = ZoomMtgEmbedded.createClient();

  // Parse schedule data if provided
  const sessionDate = scheduleAt ? formatDate(scheduleAt) : (legacySessionDate || '');
  const sessionTime = scheduleAt ? formatTime(scheduleAt) : (legacySessionTime || '');
  const sessionDuration = zoomDuration !== undefined ? formatDuration(zoomDuration) : (legacySessionDuration || '');

  // Configuration from environment variables
  const sdkKey = import.meta.env.VITE_ZOOM_SDK_KEY;
  const apiUrl = import.meta.env.VITE_API_URL;
  const registrantToken = '';
  const zakToken = '';

  // View size configuration - responsive to container
  // Use larger default size to accommodate more participants
  const viewSizeConfig = {
    width: 1200, // Increased width for better gallery view
    height: 749  // Increased height to show more participants
  };

  // Calculate container height: video height + space for buttons/controls (~150px)
  // Make it flexible to accommodate all participants
  const containerHeight = viewSizeConfig.height;

  /**
   * Fetch signature from Laravel backend
   * This keeps the SDK secret secure on the server side
   */
  const fetchSignatureFromBackend = async (meetingNumber: string, role: number): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/v1/zoom/signature`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // Note: Add Authorization header when authentication is implemented
          // 'Authorization': `Bearer ${yourJwtToken}`
        },
        body: JSON.stringify({
          meetingNumber: meetingNumber,
          role: role,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data?.signature) {
        return data.data.signature;
      } else {
        throw new Error(data.message || 'Failed to get signature from backend');
      }
    } catch (error) {
      console.error('Failed to fetch signature from backend:', error);
      throw error;
    }
  };

  const startMeeting = async (signature: string): Promise<void> => {
    try {
      // Get the container element
      const meetingSDKElement = document.getElementById('meetingSDKElement');
      const meetingSDKChatElement = document.getElementById('meetingSDKChatElement');
      if (!meetingSDKElement) {
        console.error('Meeting SDK container element not found');
        return;
      }

      // Get container dimensions for responsive sizing
      const containerRect = meetingContainerRef.current?.getBoundingClientRect();
      const containerWidthValue = containerRect?.width || viewSizeConfig.width;
      const containerHeightValue = containerRect?.height || viewSizeConfig.height;
      
      // Use container dimensions, ensuring minimum size
      const videoWidth = Math.max(containerWidthValue - 40, 800); // Account for padding
      const videoHeight = Math.max(containerHeightValue - 200, 600); // Account for controls

      // Initialize the Zoom SDK with proper sizing
      // Note: Positioning is handled via CSS on the container element
      // View switching controls (Gallery, Speaker, Ribbon) should appear automatically
      // when there are 2+ participants - CSS ensures they're visible
      await client.init({
        zoomAppRoot: meetingSDKElement,
        language: 'en-US',
        patchJsMedia: true,
        leaveOnPageUnload: true,
        customize: {
          video: {
            isResizable: false, // Allow resizing to accommodate all participants
            defaultViewType: "gallery" as any, // SuspensionViewType is a const enum, using string literal with type assertion
            viewSizes: {
              default: {
                width: videoWidth,
                height: videoHeight
              },
            },
            popper: {
              disableDraggable: true,
            }
          },
          // Don't customize chat - let Zoom SDK handle it naturally (like the old code)
          participants: {
            popper: {
              disableDraggable: false,
              anchorElement: meetingSDKElement,
              // No placement specified - will be centered via JavaScript
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: meetingContainerRef.current || 'clippingParents',
                    padding: 8
                  }
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0]
                  }
                }
              ]
            }
          },
          setting: {
            popper: {
              disableDraggable: false,
              anchorElement: meetingSDKElement,
              // No placement specified - will be centered via JavaScript
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: meetingContainerRef.current || 'clippingParents',
                    padding: 8
                  }
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0]
                  }
                }
              ]
            }
          }
        }
      });

      console.log('Zoom SDK initialized successfully');

      // Prepare join options - ensure all values are strings
      const joinOptions = {
        signature: signature,
        sdkKey: sdkKey,
        meetingNumber: String(meetingNumber),
        password: String(password || ''),
        userName: String(userName),
        userEmail: String(userEmail || ''),
        tk: registrantToken || '',
        zak: zakToken || '',
      };
      
      if (!signature) {
        throw new Error('Signature is undefined or empty');
      }

      // Join the meeting
      await client.join(joinOptions);

      console.log('Joined meeting successfully');
      
      // Update state to hide join prompt
      setIsJoined(true);
      setIsJoining(false);
      
      // Ensure the SDK element uses proper sizing after join
      setTimeout(() => {
        if (sdkElementRef.current && meetingContainerRef.current) {
          const containerRect = meetingContainerRef.current.getBoundingClientRect();
          // Set width to container width, but let height be auto to accommodate buttons
          sdkElementRef.current.style.width = `${containerRect.width}px`;
          sdkElementRef.current.style.minHeight = `${viewSizeConfig.height}px`;
        }
      }, 100);
    } catch (error) {
      console.error('Failed to initialize or join meeting:', error);
      setIsJoined(false);
      setIsJoining(false);
    }
  };

  /**
   * Fetch quota data from API
   */
  const fetchQuotaData = async () => {
    try {
      setIsLoadingQuota(true);
      const response = await eventApi.getMyQuotas();
      const data = response.data as QuotaApiResponse;
      
      if (data.status === 'success' && data.data) {
        const quotaType = getQuotaType(eventType);
        const quotas = data.data.quotas || [];
        
        // Find quota for the specific event type
        const relevantQuota = quotas.find((q: QuotaData) => q.event_type === quotaType);
        
        if (relevantQuota) {
          setQuotaData(relevantQuota);
        } else {
          // If no specific quota found, try to get from summary
          const summary = data.data.summary;
          if (summary && summary[quotaType as keyof typeof summary]) {
            const quotaSummary = summary[quotaType as keyof typeof summary];
            if (quotaSummary) {
              setQuotaData({
                remaining_quota: quotaSummary.total_remaining,
                quota: quotaSummary.total_quota,
                event_type: quotaType,
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch quota data:', error);
      // Don't show error to user, just don't display quota info
    } finally {
      setIsLoadingQuota(false);
    }
  };

  /**
   * Check if user is registered for this event schedule
   */
  const checkUserRegistration = async (): Promise<boolean> => {
    if (!scheduleId) {
      // If no scheduleId provided, assume user needs to register (show modal)
      return false;
    }

    try {
      setIsCheckingRegistration(true);
      const response = await eventApi.getMyRegistrations();
      const data = response.data as any;

      if (data?.status === 'success' && data?.data) {
        const registrations = data.data.data || [];
        // Check if any registration matches the current scheduleId
        const isRegistered = registrations.some((registration: any) => {
          const registrationScheduleId = registration.event_schedule?.id || registration.fk_event_schedule_id;
          return String(registrationScheduleId) === String(scheduleId);
        });

        return isRegistered;
      }
      return false;
    } catch (error) {
      console.error('Failed to check registration status:', error);
      // On error, assume not registered and show modal
      return false;
    } finally {
      setIsCheckingRegistration(false);
    }
  };

  /**
   * Register user for the event schedule
   */
  const registerUserForSchedule = async (): Promise<void> => {
    if (!scheduleId) {
      throw new Error('Schedule ID is required for registration');
    }

    try {
      setIsRegistering(true);
      await eventApi.registerForSchedule(scheduleId);
    } catch (error: any) {
      console.error('Failed to register for schedule:', error);
      throw error;
    } finally {
      setIsRegistering(false);
    }
  };

  const handleJoinClick = async () => {
    // If scheduleId is provided, check registration status first
    if (scheduleId) {
      const isRegistered = await checkUserRegistration();
      
      if (isRegistered) {
        // User is already registered, start meeting directly
        try {
          setIsJoining(true);
          
          if (!sdkKey) {
            console.error('Zoom SDK Key is required. Please check your .env file.');
            setIsJoining(false);
            return;
          }

          if (!apiUrl) {
            console.error('API URL is required. Please check your .env file.');
            setIsJoining(false);
            return;
          }

          if (!meetingNumber) {
            console.error('Meeting number is required.');
            setIsJoining(false);
            return;
          }

          if (!password) {
            console.error('Meeting password is required.');
            setIsJoining(false);
            return;
          }

          if (!userName) {
            console.error('User name is required.');
            setIsJoining(false);
            return;
          }

          // Fetch signature from backend and start meeting
          const signature = await fetchSignatureFromBackend(meetingNumber, role);
          await startMeeting(signature);
        } catch (error) {
          console.error('Error starting meeting:', error);
          setIsJoining(false);
        }
        return;
      }
    }

    // User is not registered or scheduleId not provided, show modal
    setShowModal(true);
    fetchQuotaData();
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleConfirmJoin = async () => {
    try {
      // If scheduleId is provided and user is not registered, register first
      if (scheduleId) {
        try {
          await registerUserForSchedule();
        } catch (error: any) {
          console.error('Error registering for schedule:', error);
          // Show error message to user
          const errorMessage = error.response?.data?.message || 'Failed to register for this event. Please try again.';
          alert(errorMessage);
          return;
        }
      }

      setShowModal(false);
      setIsJoining(true);

      // Validate configuration
      if (!sdkKey) {
        console.error('Zoom SDK Key is required. Please check your .env file.');
        setIsJoining(false);
        return;
      }

      if (!apiUrl) {
        console.error('API URL is required. Please check your .env file.');
        setIsJoining(false);
        return;
      }

      // Validate required meeting props
      if (!meetingNumber) {
        console.error('Meeting number is required.');
        setIsJoining(false);
        return;
      }

      if (!password) {
        console.error('Meeting password is required.');
        setIsJoining(false);
        return;
      }

      if (!userName) {
        console.error('User name is required.');
        setIsJoining(false);
        return;
      }

      // Fetch signature from backend and start meeting
      const signature = await fetchSignatureFromBackend(meetingNumber, role);
      await startMeeting(signature);
    } catch (error) {
      console.error('Error initializing meeting:', error);
      setIsJoining(false);
    }
  };

  useEffect(() => {
    // Ensure proper sizing when joined state changes
    if (isJoined && sdkElementRef.current && meetingContainerRef.current) {
      const updateSize = () => {
        if (sdkElementRef.current && meetingContainerRef.current) {
          const containerRect = meetingContainerRef.current.getBoundingClientRect();
          // Set width to container width, but let height be auto to accommodate buttons
          sdkElementRef.current.style.width = `${containerRect.width}px`;
          sdkElementRef.current.style.minHeight = `${viewSizeConfig.height}px`;
        }
      };
      
      // Update size immediately and on resize
      updateSize();
      window.addEventListener('resize', updateSize);
      
      return () => {
        window.removeEventListener('resize', updateSize);
      };
    }
  }, [isJoined]);

  useEffect(() => {
    // Center Zoom SDK popups (Participants, Settings, Caption Settings) when they appear
    if (!isJoined || !meetingContainerRef.current) return;

    const container = meetingContainerRef.current;
    
    // Function to center a popup element (excluding chat)
    const centerPopup = (element: HTMLElement, forceRecenter = false) => {
      // Skip if already processed (unless forcing re-center for scroll events)
      if (element.dataset.centered === 'true' && !forceRecenter) return;
      
      // Skip chat panel completely - let Zoom SDK handle it naturally
      const placement = element.getAttribute('data-popper-placement');
      const classListLower = element.classList.toString().toLowerCase();
      const elementText = element.textContent?.toLowerCase() || '';
      
      // Comprehensive chat detection - skip ALL chat-related elements
      const isChatPanel = element.classList.contains('zm-chat-panel') || 
                         element.querySelector('.zm-chat-panel') ||
                         element.closest('.zm-chat-panel') ||
                         classListLower.includes('zm-chat') ||
                         classListLower.includes('chat-panel') ||
                         (classListLower.includes('chat') && (classListLower.includes('panel') || classListLower.includes('popup'))) ||
                         placement?.includes('right') ||
                         (elementText.includes('chat') && (classListLower.includes('panel') || classListLower.includes('popup')));
      
      if (isChatPanel) {
        // Remove any centering styles we may have added
        if (element.dataset.centered === 'true') {
          element.removeAttribute('data-centered');
          element.removeAttribute('data-popup-id');
          // Remove inline centering styles
          element.style.removeProperty('position');
          element.style.removeProperty('left');
          element.style.removeProperty('top');
          element.style.removeProperty('transform');
          element.style.removeProperty('margin');
        }
        return; // Don't touch chat panel at all - let Zoom SDK handle it
      }

      const classList = element.classList.toString().toLowerCase();
      
      // More aggressive detection: Check for any popper element or Zoom SDK popup
      const hasPopperPlacement = element.hasAttribute('data-popper-placement');
      const isZoomPopup = element.closest('[data-popper-placement]') !== null;
      
      // Check if element is Participants, Settings, or Caption popup
      const isParticipantPopup = (classList.includes('participant') || elementText.includes('participant')) && 
                                 (classList.includes('list') || 
                                  classList.includes('panel') || 
                                  classList.includes('popup') ||
                                  classList.includes('modal') ||
                                  hasPopperPlacement);
      
      const isSettingsPopup = (classList.includes('settings') || elementText.includes('settings')) && 
                              (classList.includes('panel') || 
                               classList.includes('popup') ||
                               classList.includes('modal') ||
                               classList.includes('dialog') ||
                               hasPopperPlacement ||
                               element.querySelector('[class*="settings"]'));
      
      const isCaptionPopup = (classList.includes('caption') || 
                              elementText.includes('caption') ||
                              element.getAttribute('aria-label')?.toLowerCase().includes('caption') ||
                              element.getAttribute('role') === 'dialog' && elementText.includes('caption')) && 
                             (classList.includes('settings') ||
                              classList.includes('panel') || 
                              classList.includes('popup') ||
                              classList.includes('modal') ||
                              classList.includes('dialog') ||
                              element.getAttribute('role') === 'dialog' ||
                              hasPopperPlacement ||
                              classList.includes('popper'));
      
      // Also check for any popper element that's positioned on the left side
      const isLeftPositionedPopup = hasPopperPlacement && 
                                    placement && 
                                    !placement.includes('right') &&
                                    !placement.includes('top') &&
                                    !placement.includes('bottom');
      
      // Check if it's a visible popup panel (not video or control elements)
      const isVisiblePopup = element.offsetWidth > 200 && 
                            element.offsetHeight > 100 &&
                            window.getComputedStyle(element).display !== 'none' &&
                            window.getComputedStyle(element).visibility !== 'hidden';
      
      const isTargetPopup = isParticipantPopup || isSettingsPopup || isCaptionPopup || 
                           (isLeftPositionedPopup && isVisiblePopup);

      if (isTargetPopup) {
        // Skip video elements and controls (but allow settings/participant popups)
        const isVideoElement = classList.includes('video') && 
                              !classList.includes('settings') && 
                              !classList.includes('participant') &&
                              !classList.includes('caption') &&
                              !elementText.includes('settings') &&
                              !elementText.includes('participant');
        
        if (isVideoElement && !hasPopperPlacement) {
          return;
        }
        
        const containerRect = container.getBoundingClientRect();
        
        // Center the popup within the container
        const centerX = containerRect.left + containerRect.width / 2;
        const centerY = containerRect.top + containerRect.height / 2;
        
        // Apply centering styles with !important to override SDK positioning
        // Use a style element to ensure styles persist
        const styleId = `zoom-popup-center-${element.dataset.centered || 'new'}`;
        let styleElement = document.getElementById(styleId) as HTMLStyleElement;
        
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        
        // Create a unique class name for this popup
        const popupId = element.id || `popup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        if (!element.id) {
          element.id = popupId;
        }
        
        // Update CSS rule to force centering (recalculate on each call for scroll events)
        styleElement.textContent = `
          #${popupId} {
            position: fixed !important;
            left: ${centerX}px !important;
            top: ${centerY}px !important;
            transform: translate(-50%, -50%) !important;
            margin: 0 !important;
            z-index: 9999 !important;
          }
        `;
        
        // Store the popup ID for later updates
        if (!element.dataset.popupId) {
          element.dataset.popupId = popupId;
        }
        
        // Also apply inline styles as backup
        element.style.setProperty('position', 'fixed', 'important');
        element.style.setProperty('left', `${centerX}px`, 'important');
        element.style.setProperty('top', `${centerY}px`, 'important');
        element.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
        element.style.setProperty('margin', '0', 'important');
        element.style.setProperty('z-index', '9999', 'important');
        element.dataset.centered = 'true';
        element.dataset.popupId = popupId;
        
        console.log('Centered popup:', element.className, elementText.substring(0, 50));
      }
    };

    // Function to check and center all popups
    const checkAndCenterPopups = () => {
      // Check all popups (excluding chat - let Zoom SDK handle chat naturally)
      const allPopups = document.querySelectorAll(`
        [data-popper-placement]:not([data-popper-placement*="right"]),
        [role="dialog"]:not([aria-label*="chat"]):not([aria-label*="Chat"]),
        [class*="participant"]:not([class*="chat"]), [class*="Participant"]:not([class*="Chat"]),
        [class*="settings"]:not([class*="chat"]), [class*="Settings"]:not([class*="Chat"]),
        [class*="caption"]:not([class*="chat"]), [class*="Caption"]:not([class*="Chat"]),
        [class*="panel"]:not([class*="chat"]):not([class*="Chat"]), [class*="Panel"]:not([class*="chat"]):not([class*="Chat"]),
        [class*="popup"]:not([class*="chat"]):not([class*="Chat"]), [class*="Popup"]:not([class*="chat"]):not([class*="Chat"]),
        [class*="modal"]:not([class*="chat"]):not([class*="Chat"]), [class*="Modal"]:not([class*="chat"]):not([class*="Chat"]),
        [class*="dialog"]:not([class*="chat"]):not([class*="Chat"]), [class*="Dialog"]:not([class*="chat"]):not([class*="Chat"]),
        [class*="popper"]:not([class*="chat"]):not([class*="Chat"]), [class*="Popper"]:not([class*="chat"]):not([class*="Chat"])
      `);
      
      allPopups.forEach((popup) => {
        if (popup instanceof HTMLElement) {
          // Double-check: Skip chat panel completely - don't touch it at all
          const popupClassList = popup.classList.toString().toLowerCase();
          const popupText = popup.textContent?.toLowerCase() || '';
          const popupAriaLabel = popup.getAttribute('aria-label')?.toLowerCase() || '';
          const popupRole = popup.getAttribute('role');
          
          const isChatPanel = popup.classList.contains('zm-chat-panel') || 
                             popup.querySelector('.zm-chat-panel') ||
                             popup.closest('.zm-chat-panel') ||
                             popupClassList.includes('zm-chat') ||
                             popupClassList.includes('chat-panel') ||
                             (popupClassList.includes('chat') && (popupClassList.includes('panel') || popupClassList.includes('popup'))) ||
                             popup.getAttribute('data-popper-placement')?.includes('right') ||
                             (popupText.includes('chat') && (popupClassList.includes('panel') || popupClassList.includes('popup'))) ||
                             popupAriaLabel.includes('chat');
          
          if (isChatPanel) {
            return; // Skip chat panel - don't center it
          }
          
          // Check if it's a caption settings dialog (role="dialog" with caption in aria-label)
          const isCaptionDialog = popupRole === 'dialog' && 
                                 (popupAriaLabel.includes('caption') || 
                                  popupText.includes('caption') ||
                                  popupClassList.includes('caption'));
          
          // Check if it's within the container or a Zoom SDK popup
          const isInContainer = container.contains(popup);
          const hasPopperPlacement = popup.hasAttribute('data-popper-placement');
          const rootNode = popup.getRootNode();
          const isZoomSDKPopup = popup.closest('#meetingSDKElement') !== null || 
                                 (rootNode instanceof Document);
          
          // Process if it's a Zoom SDK popup (even if not in container, since SDK appends to body)
          // Also process caption dialogs specifically
          if (isCaptionDialog || hasPopperPlacement || isZoomSDKPopup || !isInContainer) {
            centerPopup(popup);
          }
        }
      });
    };

    // Observe for new popups in the entire document (since Zoom SDK appends to body)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Small delay to ensure styles are applied
            setTimeout(() => {
              centerPopup(node);
              checkAndCenterPopups();
            }, 10);
          }
        });
        
        // Also check for attribute changes (like data-popper-placement being added)
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          const target = mutation.target as HTMLElement;
          setTimeout(() => {
            centerPopup(target);
          }, 10);
        }
      });
    });

    // Start observing the entire document body (where Zoom SDK appends popups)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-popper-placement']
    });

    // Also check existing popups more frequently
    const checkInterval = setInterval(() => {
      checkAndCenterPopups();
    }, 200);
    
    // Initial check
    setTimeout(() => {
      checkAndCenterPopups();
    }, 500);

    // Re-center popups on scroll events
    const handleScroll = () => {
      // Small delay to let Zoom SDK finish its positioning
      setTimeout(() => {
        // Force re-center all popups on scroll (excluding chat)
        const allPopups = document.querySelectorAll('[data-centered="true"]');
        allPopups.forEach((popup) => {
          if (popup instanceof HTMLElement) {
            // Skip chat panel
            const popupClassList = popup.classList.toString().toLowerCase();
            const isChatPanel = popup.classList.contains('zm-chat-panel') || 
                               popup.querySelector('.zm-chat-panel') ||
                               popup.closest('.zm-chat-panel') ||
                               popupClassList.includes('zm-chat') ||
                               popupClassList.includes('chat-panel') ||
                               (popupClassList.includes('chat') && popupClassList.includes('panel'));
            
            if (!isChatPanel) {
              centerPopup(popup, true); // Force re-center
            }
          }
        });
        checkAndCenterPopups();
      }, 50);
    };

    // Listen for scroll events on window and container
    window.addEventListener('scroll', handleScroll, true); // Use capture phase
    if (container) {
      container.addEventListener('scroll', handleScroll, true);
    }

    // Also listen for resize events which might trigger repositioning
    const handleResize = () => {
      setTimeout(() => {
        checkAndCenterPopups();
      }, 100);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      clearInterval(checkInterval);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      if (container) {
        container.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [isJoined]);

  useEffect(() => {
    // Cleanup on component unmount
    return () => {
      if (client) {
        try {
          // Leave the meeting when component unmounts
          client.leaveMeeting().catch((error: unknown) => {
            console.error('Error leaving meeting:', error);
          });
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    };
  }, []);

  return (
    <>
      {/* Join Prompt - only show when not joined */}
      {!isJoined && (
        <div className="zoom-join-prompt">
          <h2>Ready to Join?</h2>
          <p>You're eligible for this session. Click below to enter the live session room.</p>
          <button 
            onClick={handleJoinClick} 
            className="join-live-button"
            disabled={isCheckingRegistration || isJoining}
          >
            {isJoining ? (
              <>
                <span className="loading-spinner" style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  marginRight: '8px'
                }}></span>
                Joining Session...
              </>
            ) : (
              <>
                <img src="/play_icon.svg" alt="Play" className="play-icon" />
                {isCheckingRegistration ? 'Checking...' : 'Join Live Session Now'}
              </>
            )}
          </button>
          {isJoining && (
            <p style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
              Please wait while we connect you to the session...
            </p>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="confirm-join-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Join Session</h2>
              <button className="modal-close" onClick={handleCancel}>×</button>
            </div>
            
            <p className="modal-intro">
              You're about to join this live session. Please confirm to continue.
            </p>
            <div className="modal-content">
              {/* Session Details Card */}
              <div className="session-details-card">
                <h3>Session Details</h3>
                {sessionTitle && <p className="session-topic">{sessionTitle}</p>}
                <div className="session-meta">
                  {(sessionDate || sessionTime) && (
                    <div className="meta-item">
                      <img src="/calendar.svg" alt="Calendar" width="16" height="16" />
                      <span>{sessionDate && sessionTime ? `${sessionDate} • ${sessionTime}` : sessionDate || sessionTime}</span>
                    </div>
                  )}
                  {sessionDuration && (
                    <div className="meta-item">
                      <img src="/clock.svg" alt="Clock" width="16" height="16" />
                      <span>{sessionDuration}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Session Quota Card */}
              <div className="session-quota-card">
                <div className="quota-header">
                  <img src="/warning_icon.svg" alt="Warning" width="20" height="20" />
                  <h3>Session Quota</h3>
                </div>
                {isLoadingQuota ? (
                  <p>Loading quota information...</p>
                ) : quotaData ? (
                  <p>
                    You have <strong>{quotaData.remaining_quota} {quotaData.remaining_quota === 1 ? 'session slot' : 'session slots'}</strong> remaining this month. Joining this session will use <strong>1 slot</strong>. After joining, you'll have <strong>{Math.max(0, quotaData.remaining_quota - 1)} {Math.max(0, quotaData.remaining_quota - 1) === 1 ? 'slot' : 'slots'}</strong> left.
                  </p>
                ) : (
                  <p>
                    Unable to load quota information. Please try again later.
                  </p>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-button" 
                onClick={handleCancel}
                disabled={isRegistering}
              >
                Cancel
              </button>
              <button 
                className="confirm-button" 
                onClick={handleConfirmJoin}
                disabled={isRegistering}
              >
                {isRegistering ? (
                  <>
                    <span>Registering...</span>
                  </>
                ) : (
                  <>
                    <img src="/play.svg" alt="Play" className="play-icon-small" />
                    Confirm & Join
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zoom Meeting Container - takes full size when joined */}
      <div 
        ref={meetingContainerRef}
        className="zoom-meeting-container" 
        style={{ 
          width: '100%', 
          height: isJoined ? '100%' : '0',
          minHeight: isJoined ? `${containerHeight}px` : '0',
          maxHeight: isJoined ? 'none' : '0',
          display: isJoined ? 'block' : 'none',
          position: 'relative',
          overflow: 'auto', // Allow scrolling if content exceeds container
          zIndex: isJoined ? 1 : -1
        }}
      >
        {/* Zoom SDK will inject its UI into this div */}
        <div 
          ref={sdkElementRef}
          id="meetingSDKElement" 
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: isJoined ? `${viewSizeConfig.height}px` : '0',
            position: 'relative',
            display: 'block',
            margin: 0,
            padding: 0,
            border: 'none',
            outline: 'none',
            boxSizing: 'border-box',
            overflow: 'auto' // Allow scrolling for gallery view
          }}
        ></div>
        {/* Chat anchor element - positioned inside the container */}
        <div 
          id="meetingSDKChatElement"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '1px',
            height: '1px',
            pointerEvents: 'none',
            visibility: 'hidden'
          }}
        ></div>
      </div>
      
      {/* Inject styles to ensure Zoom SDK widget fills container and shows all participants */}
      {isJoined && (
        <style>{`
          #meetingSDKElement,
          #meetingSDKElement > * {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .zoom-meeting-container {
            width: 100% !important;
            overflow: auto !important;
            position: relative !important;
            min-height: ${containerHeight}px !important;
          }
          /* Ensure gallery view can expand to show all participants */
          #meetingSDKElement [class*="gallery"],
          #meetingSDKElement [class*="Gallery"],
          #meetingSDKElement [class*="video-container"],
          #meetingSDKElement [class*="VideoContainer"],
          #meetingSDKElement [class*="participant"],
          #meetingSDKElement [class*="Participant"] {
            display: flex !important;
            flex-wrap: wrap !important;
            width: 100% !important;
            min-height: auto !important;
            overflow: visible !important;
          }
          /* Ensure video tiles can wrap and show all participants */
          #meetingSDKElement [class*="video-tile"],
          #meetingSDKElement [class*="VideoTile"],
          #meetingSDKElement [class*="video-item"],
          #meetingSDKElement [class*="VideoItem"] {
            flex: 0 0 auto !important;
            min-width: 200px !important;
            max-width: 300px !important;
          }
          /* Ensure poppers have proper z-index (excluding chat - let Zoom SDK handle it) */
          [data-popper-placement]:not([data-popper-placement*="right"]),
          [data-popper-placement]:not([data-popper-placement*="right"]) > *,
          .zm-popper:not(.zm-chat-panel) {
            z-index: 9999 !important;
          }
          
          /* Don't touch chat panel - let Zoom SDK handle it naturally like the old code */
          /* Ensure view switching controls are visible */
          button[aria-label*="Gallery"],
          button[aria-label*="gallery"],
          button[aria-label*="Speaker"],
          button[aria-label*="speaker"],
          button[aria-label*="Ribbon"],
          button[aria-label*="ribbon"],
          button[title*="Gallery"],
          button[title*="gallery"],
          button[title*="Speaker"],
          button[title*="speaker"],
          button[title*="Ribbon"],
          button[title*="ribbon"],
          [class*="view-switch"],
          [class*="ViewSwitch"],
          [class*="view-toggle"],
          [class*="ViewToggle"],
          [class*="gallery-view-button"],
          [class*="speaker-view-button"],
          [class*="ribbon-view-button"] {
            display: inline-flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            z-index: 1000 !important;
          }
          /* Ensure toolbar/controls bar is visible */
          [class*="toolbar"],
          [class*="Toolbar"],
          [class*="control-bar"],
          [class*="ControlBar"],
          [class*="meeting-controls"],
          [class*="MeetingControls"] {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Ensure parent containers allow overflow */
          .coaching-details-page,
          .zoom-hero,
          .zoom-container {
            overflow-x: visible !important;
            overflow-y: auto !important;
            position: relative !important;
            min-height: ${containerHeight}px !important;
          }
        `}</style>
      )}

      {/* Loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Customize the height of the zoom meeting container and ensure all participants are visible */}
        <style>
        {`
          .css-108w4cc {
            height: auto !important;
            min-height: ${containerHeight}px !important;
            max-height: none !important;
          }

          /* Ensure gallery view container expands to show all participants */
          [class*="gallery-view"],
          [class*="GalleryView"],
          [class*="video-gallery"],
          [class*="VideoGallery"] {
            display: flex !important;
            flex-wrap: wrap !important;
            width: 100% !important;
            min-height: auto !important;
            height: auto !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            gap: 8px !important;
            padding: 8px !important;
          }

          /* Ensure participant video tiles are visible and properly sized */
          [class*="video-tile"],
          [class*="VideoTile"],
          [class*="participant-video"],
          [class*="ParticipantVideo"] {
            flex: 0 0 calc(25% - 8px) !important;
            min-width: 200px !important;
            max-width: 300px !important;
            aspect-ratio: 16 / 9 !important;
            margin: 4px !important;
          }

          /* Ensure view switching controls (Gallery, Speaker, Ribbon) are visible */
          button[aria-label*="Gallery"],
          button[aria-label*="gallery"],
          button[aria-label*="Speaker"],
          button[aria-label*="speaker"],
          button[aria-label*="Ribbon"],
          button[aria-label*="ribbon"],
          button[aria-label*="View"],
          button[aria-label*="view"],
          button[title*="Gallery"],
          button[title*="gallery"],
          button[title*="Speaker"],
          button[title*="speaker"],
          button[title*="Ribbon"],
          button[title*="ribbon"],
          button[title*="View"],
          button[title*="view"],
          [class*="view-switch"],
          [class*="ViewSwitch"],
          [class*="view-toggle"],
          [class*="ViewToggle"],
          [class*="gallery-view-button"],
          [class*="speaker-view-button"],
          [class*="ribbon-view-button"],
          [class*="view-mode"],
          [class*="ViewMode"],
          [data-testid*="gallery"],
          [data-testid*="speaker"],
          [data-testid*="ribbon"] {
            display: inline-flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            z-index: 1000 !important;
            pointer-events: auto !important;
          }

          /* Ensure toolbar/controls bar is visible and contains view controls */
          [class*="toolbar"],
          [class*="Toolbar"],
          [class*="control-bar"],
          [class*="ControlBar"],
          [class*="meeting-controls"],
          [class*="MeetingControls"],
          [class*="video-controls"],
          [class*="VideoControls"],
          [class*="view-controls"],
          [class*="ViewControls"] {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
          }

          /* Hide Share Screen button */
          button[aria-label="Share Screen"],
          button[title="Share Screen"] {
            display: none !important;
          }

          /* Hide Start Video button (but keep Audio button visible) */
          button[title="Start Video"] {
            display: none !important;
          }
            
          .css-15yt4jb{
           min-width: 0 !important;
          }

          .css-woj72k > .zoom-MuiBox-root:not(:last-child) {
            margin-right: 0 !important;
          }

          .css-ip80nc{
            width: 100% !important;
          }

          .css-fypbnh{
            z-index: 9999 !important;
          }

          .css-12efcmn{
          width: 100% !important;
          height: 100% !important;
          }
          
          .css-1ex9ano{
           max-width: 376px !important;
          }

          .css-15yt4jb{
            display: none !important;
          }
            
        `}
      </style>
    </>
  );
};

export default ZoomMeeting;

